
import React, { memo } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RefreshCw, User, Wifi, WifiOff } from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

interface ConversationHeaderProps {
  conversationTitle: string;
  reagentName?: string;
  onBack: () => void;
  onRefresh: () => void;
  isLoading: boolean;
  isRealtimeConnected?: boolean;
}

const ConversationHeader: React.FC<ConversationHeaderProps> = ({
  conversationTitle,
  reagentName,
  onBack,
  onRefresh,
  isLoading,
  isRealtimeConnected = false
}) => {
  return (
    <div className="p-4 border-b flex items-center justify-between bg-card/50 backdrop-blur-sm sticky top-0 z-10">
      <div className="flex items-center">
        <Button variant="ghost" size="icon" onClick={onBack} className="mr-2 md:mr-3 md:hidden">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
            <User className="h-5 w-5 text-primary/70" />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center">
              <h2 className="font-medium text-lg line-clamp-1 mr-2">{conversationTitle}</h2>
              {isRealtimeConnected && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="px-1.5 h-5 bg-green-50 border-green-200">
                        <Wifi className="h-3 w-3 text-green-600 mr-1" />
                        <span className="text-xs text-green-700">Live</span>
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Real-time updates active</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {reagentName && !conversationTitle.includes(reagentName) && (
              <p className="text-sm text-muted-foreground line-clamp-1">Re: {reagentName}</p>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onRefresh}
                disabled={isLoading}
                className="ml-2"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Refresh conversation</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default memo(ConversationHeader);
